import { cn } from "@/modules/lib/utils"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { getFormattedCouponReduction } from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import dayjs from "dayjs"
import { Banknote, Percent, Tag } from "lucide-react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramSubscriberDetailsContentPriceProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberDetailsContentPrice({
  subscriberDetails,
  className,
  ...props
}: ProgramSubscriberDetailsContentPriceProps) {
  const language = useLanguage()

  return (
    <div className={cn("flex w-full flex-col gap-2", className)} {...props}>
      <div className="flex w-full items-baseline gap-2">
        <Text fontStyle="BS" className="text-textDefault-caption">
          {word("program.subscribers.subscriptionDetails.price.label")}
        </Text>
        <div className="flex grow border-t border-borderDefault" />
      </div>
      {subscriberDetails.paidPrice && !subscriberDetails.trial ? (
        <div className="grid grid-cols-12">
          <div className="col-span-6">
            {subscriberDetails.nextPayment && subscriberDetails.nextPayment.value >= 0 ? (
              <>
                <div className="flex flex-row items-center gap-1 py-1">
                  <Banknote className="text-textDefault-caption" size={16} />
                  <Text fontStyle="LS" className="text-textDefault-caption">
                    {word("program.subscribers.subscriptionDetails.nextPayment.label")}
                  </Text>
                </div>
                <div className="flex flex-row items-center gap-2 py-1">
                  <Text fontStyle="LM" className="text-textDefault-heading">
                    {dayjs(subscriberDetails.nextPaymentDate).format("L")}
                  </Text>
                  <div className="flex flex-row items-baseline gap-1">
                    <Text fontStyle="LL" className="text-textDefault-heading">
                      {formatCurrency(
                        subscriberDetails.nextPayment.value,
                        subscriberDetails.nextPayment.currency,
                        language,
                      )}
                    </Text>
                    {subscriberDetails.referencePrice &&
                    subscriberDetails.nextPayment &&
                    subscriberDetails.referencePrice.value !== subscriberDetails.nextPayment.value ? (
                      <Text fontStyle="LM" className="text-textDefault-caption line-through">
                        {formatCurrency(
                          subscriberDetails.referencePrice.value,
                          subscriberDetails.referencePrice.currency,
                          language,
                        )}
                      </Text>
                    ) : null}
                  </div>
                </div>
              </>
            ) : (
              <Text fontStyle="LL" className="text-textDefault-heading">
                {subscriberDetails.getWordingPrice(language)}
              </Text>
            )}
          </div>
          <div className="col-span-6">
            <div className="flex flex-row items-center justify-end gap-1 py-1">
              <Text fontStyle="LS" className="font-semibold text-textDefault-caption">
                {word("program.subscribers.subscriptionDetails.lastPayment.label")}
              </Text>
            </div>
            {subscriberDetails.lastInvoices && subscriberDetails.lastInvoices.length > 0 ? (
              subscriberDetails.lastInvoices.map((invoice, index) => (
                <div key={index} className="flex flex-row items-center justify-end gap-1 py-1">
                  <Text fontStyle="LS" className="text-textDefault-caption">
                    ({dayjs(invoice.invoiceDate).format("L")})
                  </Text>
                  <Text fontStyle="LS" className="text-textDefault-caption">
                    {formatCurrency(invoice.price.value, invoice.price.currency, language)}
                  </Text>
                </div>
              ))
            ) : (
              <Text
                fontStyle="LS"
                className="flex flex-row items-center justify-end gap-1 py-1 text-textDefault-caption"
              >
                {word("program.subscribers.subscriptionDetails.noInvoice.label")}
              </Text>
            )}
          </div>
        </div>
      ) : (
        <Text fontStyle="LL" className="text-textDefault-heading">
          {subscriberDetails.getWordingPrice(language)}
        </Text>
      )}

      <div className="flex w-full flex-col gap-3">
        {/* PROMO CODE */}
        {subscriberDetails.usedPromoCode && subscriberDetails.paidPrice ? (
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-center gap-1 pl-3">
              <Percent size={15} className="text-textDefault-caption" />
              <Text fontStyle="LS" className="text-textDefault-caption">
                {word("box.member.table.offer.item.codePromo")}
              </Text>
            </div>
            <div className="flex flex-row items-baseline gap-1 pl-3">
              <Text fontStyle="LM" className="text-textDefault-heading">
                {subscriberDetails.usedPromoCode.code}
              </Text>
              <Text fontStyle="LS" className="text-textDefault-caption">
                -
                {getFormattedCouponReduction(
                  subscriberDetails.usedPromoCode.type,
                  subscriberDetails.usedPromoCode.value,
                  language,
                  subscriberDetails.paidPrice.currency,
                )}
                {subscriberDetails.usedPromoCode.type === CouponType.PERCENT
                  ? ` = -${subscriberDetails.getWordingReductionForPromoCode(language)}`
                  : null}
              </Text>
            </div>
            <div className="flex flex-row items-baseline gap-1 pl-3">
              <Text fontStyle="LS" className="text-textDefault-caption">
                {`(${subscriberDetails.usedPromoCode.getWordingDate()})`}
              </Text>
            </div>
          </div>
        ) : null}

        {/* DISCOUNT */}
        {subscriberDetails.coupon && subscriberDetails.referencePrice ? (
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-center gap-1 pl-3">
              <Tag size={15} className="text-textDefault-caption" />
              <Text fontStyle="LS" className="font-semibold text-textDefault-caption">
                {word("box.member.table.offer.item.discount.label")}
              </Text>
            </div>
            <div className="flex flex-col gap-1 pl-3">
              <Text fontStyle="BS" className="text-textDefault-heading">
                -
                {getFormattedCouponReduction(
                  subscriberDetails.coupon.type,
                  subscriberDetails.coupon.value,
                  language,
                  subscriberDetails.referencePrice.currency,
                )}
                {subscriberDetails.coupon.type === CouponType.PERCENT
                  ? ` = -${subscriberDetails.getWordingReductionForDiscount(language)}`
                  : null}
              </Text>
              <Text fontStyle="LS" className="text-textDefault-caption">
                {subscriberDetails.getWordingCouponDate()}
              </Text>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
